import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { httpService } from '../utility/httpService';
import { httpConstants } from '../constants';
import { API_BASE } from '../config/env';
import { updateDiagramData } from '../features/BpmnIoDiagram/store/diagramViewerSlice';
import ShowToast, { ToastTypeEnum } from '../common/components/ShowToast';

// Centralizes polling and syncing logic used by Diagram Index and TaxonomyXPLR
export const useRegenerateSolutions = () => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();

  const clearExistingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const checkStatus = useCallback(async (subprocessId: string): Promise<string> => {
    try {
      const res = await httpService<{
        id: string;
        name: string;
        status: string;
        diagram: Record<string, any>;
        aisolution: Record<string, any>;
      }>(
        httpConstants.METHOD_TYPE.GET,
        `${API_BASE}/api/v1/e2e_taxonomy/level2/${subprocessId}/status/`,
        {}
      );
      return res?.status || '';
    } catch (error) {
      console.error('Error checking status:', error);
      return '';
    }
  }, []);

  // Polls subprocess until completed/failed; calls onCompleted when done
  const startPolling = useCallback(
    (
      subprocessId: string,
      onCompleted?: (payload: { diagram?: any; aisolution?: any }) => Promise<void> | void
    ) => {
      clearExistingInterval();

      const checkAndHandle = async () => {
        const status = await checkStatus(subprocessId);

        if (['opportunities_in_progress', 'aisolutions_in_progress'].includes(status)) {
          // Keep polling only while in progress
          return;
        }

        // Stop polling once status is not in progress
        clearExistingInterval();
        if (status === 'aisolutions_generated') {
          try {
            const res = await httpService<{
              id: string;
              name: string;
              status: string;
              diagram: Record<string, any>;
              aisolution: Record<string, any>;
            }>(
              httpConstants.METHOD_TYPE.GET,
              `${API_BASE}/api/v1/e2e_taxonomy/level2/${subprocessId}/status/`,
              {}
            );
            await onCompleted?.({ diagram: res?.diagram, aisolution: res?.aisolution });
          } catch (e) {
            console.error('Error fetching final status payload:', e);
            await onCompleted?.({});
          }
        } else if (status === 'failed' || status === 'error') {
          ShowToast({ message: 'Opportunities regeneration failed', type: ToastTypeEnum.Error });
        }
      };
      // Run once immediately
      checkAndHandle();
      // Continue polling only while in progress
      pollingIntervalRef.current = setInterval(checkAndHandle, 20000);

      return () => clearExistingInterval();
    },
    [checkStatus, clearExistingInterval]
  );

  // Fetch opportunities for a diagram and sync them into Redux
  const fetchOpportunitiesAndSync = useCallback(
    async (diagramId: string | undefined, diagramData: any, showToast: boolean = false) => {
      if (!diagramId) return;
      try {
        const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramId}`;
        const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);
        const mapped = Array.isArray(response?.results)
          ? response.results.map((opp: any) => ({
              id: opp.id,
              opportunity_title: opp.opportunity_name || '',
              opportunity_description: opp.description || '',
              affected_process_stages: opp.affected_process_stages || [],
              expected_business_value: opp.expected_business_value || [],
            }))
          : [];
        // Avoid dispatch loops by shallow comparing the array length and ids
        const prev = diagramData?.custom_opportunities || [];
        const isSame =
          Array.isArray(prev) &&
          prev.length === mapped.length &&
          prev.every((p: any, idx: number) => p?.id === mapped[idx]?.id);
        if (!isSame) {
          dispatch(
            updateDiagramData({
              ...diagramData,
              custom_opportunities: mapped,
            })
          );
        }
        if (showToast) {
          ShowToast({
            message: 'Opportunities refreshed successfully',
            type: ToastTypeEnum.Success,
          });
        }
      } catch (error) {
        console.error('Error fetching opportunities:', error);
        if (showToast) {
          ShowToast({ message: 'Failed to fetch opportunities', type: ToastTypeEnum.Error });
        }
      }
    },
    [dispatch]
  );

  // Polls selectedSubprocess and updates via provided setter
  const pollSelectedSubprocessStatus = useCallback(
    (selectedSubprocess: any, setSelectedSubprocess: (s: any) => void) => {
      if (!selectedSubprocess) return () => {};
      const targetIds: string[] = [selectedSubprocess]
        .flatMap((sp) => [sp])
        .filter(Boolean)
        .map((sp: any) => sp.id)
        .filter(Boolean);
      if (!targetIds.length) return () => {};
      clearExistingInterval();
      const fetchStatus = async (ids: string[]) => {
        try {
          for (const id of [...ids]) {
            try {
              const res = await httpService<{
                id: string;
                name: string;
                status: string;
                diagram: Record<string, any>;
                aisolution: Record<string, any>;
                opportunity: Record<string, any>;
              }>(
                httpConstants.METHOD_TYPE.GET,
                `${API_BASE}/api/v1/e2e_taxonomy/level2/${id}/status/`,
                {}
              );

              if (
                res?.status === selectedSubprocess.status &&
                !['opportunities_in_progress', 'aisolutions_in_progress'].includes(res?.status)
              ) {
                clearExistingInterval();
                return;
              }

              if (res?.status === 'aisolutions_generated') {
                clearExistingInterval(); // Stop polling immediately
                setSelectedSubprocess((prev: any) => ({
                  ...prev,
                  status: 'aisolutions_generated',
                  diagram: {
                    ...(prev?.diagram || {}),
                    id: res.diagram?.id ?? '',
                    newAiOpportunities: Array.isArray(res.aisolution) ? res.aisolution : [],
                  },
                }));
                return;
              }

              if (res?.status === 'opportunities_generated') {
                clearExistingInterval(); // Stop polling immediately
                setSelectedSubprocess((prev: any) => ({
                  ...prev,
                  status: 'opportunities_generated',
                }));
                return;
              }

              if (
                ['opportunities_in_progress', 'aisolutions_in_progress'].includes(res?.status) &&
                selectedSubprocess.status !== res?.status
              ) {
                setSelectedSubprocess((prev: any) => ({
                  ...prev,
                  status: res?.status,
                }));
              }
            } catch (err: any) {
              if (err?.status === 404) {
                console.warn(`Status not found for subprocess ${id}. Stopping polling.`);
                clearExistingInterval();
                return;
              }

              console.error('Error checking status for id:', id, err);
            }
          }
        } catch (err) {
          console.error('Error checking status:', err);
        }
      };
      // call immediately once
      fetchStatus([...targetIds]);
      // poll every 15 seconds only while in progress
      pollingIntervalRef.current = setInterval(() => fetchStatus([...targetIds]), 15000);
      return () => clearExistingInterval();
    },
    [clearExistingInterval]
  );

  return {
    startPolling,
    fetchOpportunitiesAndSync,
    pollSelectedSubprocessStatus,
  };
};
